import * as React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * WorkspaceMenu — small dropdown menu for workspace screens (T-0142).
 *
 * Mirrors `.menu` in `docs/requirement-library/prototype/selection.css`:
 * a paper-0 card with a 1px ink-100 border and the modal shadow, 32px
 * items, an optional uppercase label and separators. Used for "Add
 * requirements", the criticality chip on a row, and "Set criticality" in
 * the bulk bar.
 *
 * Built on Radix DropdownMenu, which provides `role="menu"`, arrow-key
 * movement, type-ahead, Esc to close and focus return. The content is
 * portalled, so it is not clipped by a scrolling grid. Clicks and keys
 * inside the menu are stopped from reaching a grid row underneath (React
 * events bubble through portals).
 */

export interface WorkspaceMenuItem {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** Muted trailing text, for example "Coming next". */
  hint?: React.ReactNode;
  disabled?: boolean;
  /** Draw a separator above this item. */
  separatorBefore?: boolean;
  /**
   * Makes the item an option that is on or off (`menuitemcheckbox`). It
   * shows a tick when on, and choosing it keeps the menu open so several
   * options can be set before an action is picked.
   */
  checked?: boolean;
  onSelect?: () => void;
}

export interface WorkspaceMenuProps {
  /** The trigger element; it must accept a ref and button props. */
  trigger: React.ReactElement;
  items: ReadonlyArray<WorkspaceMenuItem>;
  /** Small uppercase heading inside the menu. */
  label?: React.ReactNode;
  /** Accessible name for the menu. */
  'aria-label': string;
  align?: 'start' | 'end' | 'center';
  side?: 'top' | 'bottom';
  className?: string;
}

const ITEM_CLASS = cn(
  'flex h-8 cursor-pointer select-none items-center gap-2 rounded-chip px-2.5 text-body-sm text-ink-700 outline-none',
  'data-[highlighted]:bg-paper-100 data-[highlighted]:text-ink-900',
  'data-[disabled]:cursor-default data-[disabled]:text-ink-300',
);

export function WorkspaceMenu({
  trigger,
  items,
  label,
  'aria-label': ariaLabel,
  align = 'start',
  side = 'bottom',
  className,
}: WorkspaceMenuProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          aria-label={ariaLabel}
          align={align}
          side={side}
          sideOffset={4}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className={cn(
            'z-[70] min-w-[190px] rounded-card border border-ink-100 bg-paper-0 p-1 shadow-modal focus:outline-none',
            className,
          )}
        >
          {label ? (
            <DropdownMenu.Label className="px-2.5 pb-0.5 pt-1.5 text-[11px] font-medium uppercase leading-[18px] tracking-wide text-ink-500">
              {label}
            </DropdownMenu.Label>
          ) : null}
          {items.map((item) => (
            <React.Fragment key={item.id}>
              {item.separatorBefore ? (
                <DropdownMenu.Separator className="my-1 h-px bg-ink-100" />
              ) : null}
              {item.checked !== undefined ? (
                <DropdownMenu.CheckboxItem
                  checked={item.checked}
                  disabled={item.disabled}
                  onSelect={(e) => {
                    // An option, not an action: the menu stays open.
                    e.preventDefault();
                    item.onSelect?.();
                  }}
                  className={ITEM_CLASS}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-4 w-4 flex-none items-center justify-center rounded-sm border-[1.5px]',
                      item.checked
                        ? 'border-ink-900 bg-ink-900 text-paper-0'
                        : 'border-ink-300 bg-paper-0',
                    )}
                  >
                    {item.checked ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.hint ? (
                    <span className="flex-none text-meta text-ink-500">{item.hint}</span>
                  ) : null}
                </DropdownMenu.CheckboxItem>
              ) : (
                <DropdownMenu.Item
                  disabled={item.disabled}
                  onSelect={() => item.onSelect?.()}
                  className={ITEM_CLASS}
                >
                  {item.icon ? (
                    <span className="flex-none text-ink-500" aria-hidden="true">
                      {item.icon}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.hint ? (
                    <span className="flex-none text-meta text-ink-500">{item.hint}</span>
                  ) : null}
                </DropdownMenu.Item>
              )}
            </React.Fragment>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

WorkspaceMenu.displayName = 'WorkspaceMenu';
